import { PASSWORD_MIN } from '@base/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../api.ts';
import { ME_KEY, useMe } from '../auth.tsx';
import { Button, Field, Input, issueFor } from './ui.tsx';

/**
 * Troca da própria senha (ADR 0109): na primeira entrada, com a senha provisória, e quando a pessoa
 * quiser, em Configurações. As outras sessões da pessoa são encerradas pelo servidor.
 */
export function PasswordChangeForm({
  temporary = false,
  onDone,
}: {
  /** Primeira entrada: a senha atual é a provisória. */
  temporary?: boolean;
  onDone?: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const me = useMe();
  const [currentPassword, setCurrent] = useState('');
  const [newPassword, setNew] = useState('');
  const [confirm, setConfirm] = useState('');
  const change = useMutation({
    mutationFn: () => api.post<void>('/auth/password', { currentPassword, newPassword }),
    onSuccess: async () => {
      setCurrent('');
      setNew('');
      setConfirm('');
      await queryClient.invalidateQueries({ queryKey: ME_KEY });
      onDone?.();
    },
  });
  const mismatch = confirm !== '' && confirm !== newPassword;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!mismatch) change.mutate();
  };
  return (
    <form onSubmit={submit} className="flex max-w-sm flex-col gap-4" noValidate>
      {/* E-mail escondido: o navegador sabe de quem é a senha e troca a salva pela nova. */}
      <input
        type="email"
        name="username"
        autoComplete="username"
        value={me.data?.user.email ?? ''}
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        className="sr-only"
      />
      <Field
        label={temporary ? t('password.temporary') : t('password.current')}
        error={issueFor(change.error, 'currentPassword')}
      >
        <Input
          type="password"
          name="current-password"
          autoComplete="current-password"
          value={currentPassword}
          onChange={(e) => setCurrent(e.target.value)}
        />
      </Field>
      <Field
        label={t('password.new')}
        hint={t('password.newHint', { min: PASSWORD_MIN })}
        error={issueFor(change.error, 'newPassword')}
      >
        <Input
          type="password"
          name="new-password"
          autoComplete="new-password"
          minLength={PASSWORD_MIN}
          value={newPassword}
          onChange={(e) => setNew(e.target.value)}
        />
      </Field>
      <Field label={t('password.confirm')} error={mismatch ? t('password.mismatch') : undefined}>
        <Input
          type="password"
          autoComplete="new-password"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
        />
      </Field>
      {change.isSuccess && (
        <p role="status" className="text-sm text-ok">
          {t('password.changed')}
        </p>
      )}
      <Button
        type="submit"
        className="self-start"
        disabled={
          change.isPending ||
          !currentPassword ||
          newPassword.length < PASSWORD_MIN ||
          confirm !== newPassword
        }
      >
        {change.isPending ? t('common.saving') : t('password.submit')}
      </Button>
    </form>
  );
}
